import { useState } from 'react';
import { useCreatePropertyInquiry } from '@/hooks/usePropertyInquiries';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';

interface PropertyInquiryFormProps {
  propertyId: string;
  propertyTitle: string;
}

export const PropertyInquiryForm = ({ propertyId, propertyTitle }: PropertyInquiryFormProps) => {
  const createInquiry = useCreatePropertyInquiry();
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    message: '',
    preferred_contact: 'email' as 'email' | 'phone' | 'whatsapp',
    inquiry_type: 'information' as 'viewing' | 'information' | 'offer',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    await createInquiry.mutateAsync({
      property_id: propertyId,
      ...formData,
    });

    // Reset form
    setFormData({
      full_name: '',
      email: '',
      phone: '',
      message: '',
      preferred_contact: 'email',
      inquiry_type: 'information',
    });
  };

  return (
    <Card className="p-6">
      <h3 className="text-2xl font-bold mb-2">Inquire About This Property</h3>
      <p className="text-sm text-muted-foreground mb-6">{propertyTitle}</p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Label htmlFor="full_name">Full Name *</Label>
          <Input
            id="full_name"
            required
            value={formData.full_name}
            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
          />
        </div>

        <div>
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
        </div>

        <div>
          <Label htmlFor="phone">Phone *</Label>
          <Input
            id="phone"
            type="tel"
            required
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
          />
        </div>

        <div>
          <Label htmlFor="inquiry_type">I'm Interested In *</Label>
          <Select 
            value={formData.inquiry_type} 
            onValueChange={(value: any) => setFormData({ ...formData, inquiry_type: value })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="information">General Information</SelectItem>
              <SelectItem value="viewing">Schedule a Viewing</SelectItem>
              <SelectItem value="offer">Make an Offer</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label htmlFor="message">Message *</Label>
          <Textarea
            id="message"
            required
            rows={4}
            value={formData.message}
            onChange={(e) => setFormData({ ...formData, message: e.target.value })}
            placeholder="Tell us about your requirements..."
          />
        </div>

        <div>
          <Label className="mb-3 block">Preferred Contact Method</Label>
          <RadioGroup
            value={formData.preferred_contact}
            onValueChange={(value: any) => setFormData({ ...formData, preferred_contact: value })}
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="email" id="contact-email" />
              <Label htmlFor="contact-email" className="font-normal cursor-pointer">Email</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="phone" id="contact-phone" />
              <Label htmlFor="contact-phone" className="font-normal cursor-pointer">Phone</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="whatsapp" id="contact-whatsapp" />
              <Label htmlFor="contact-whatsapp" className="font-normal cursor-pointer">WhatsApp</Label>
            </div>
          </RadioGroup>
        </div>

        <Button type="submit" className="w-full" disabled={createInquiry.isPending}>
          {createInquiry.isPending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Sending...
            </>
          ) : (
            'Send Inquiry'
          )}
        </Button>
      </form>
    </Card>
  );
};
